// packages/web-ui/src/state/kb.ts
//
// Story W4-S5 (AC #6) — `kb` zustand slice. Tracks the loaded KB entity list
// + the active search/filter selection, with debounced refetch and
// AbortController-guarded inflight cancellation. Mirrors the W4-S4
// `changeset.ts` slice pattern (vanilla store factory + StateCreator).
//
// SSE wiring (`kb:changesetApplied` consumer) is implemented as a slice
// action that AppShell-level subscriptions invoke. The slice does NOT
// subscribe directly — it stays free of side-effects beyond `fetch`.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type {
  KbEntity,
  KbEntityKind,
} from '@tessa-lean/base/api-schemas/index.js';
import type { KbApplyOutcome } from '@tessa-lean/base';

import { kbApi as defaultKbApi, type KbApi } from '../api/clients/kb.js';
import type { ApiContext } from '../api/client.js';

const DEFAULT_KINDS: ReadonlyArray<KbEntityKind> = [
  'part',
  'port',
  'interface',
  'connection',
  'value-attribute',
];

/** Slice state. */
export interface KbSliceState {
  /** Loaded entities — the full set after merging per-kind responses. */
  entities: KbEntity[];
  /** Status of the most-recent refetch. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Populated when `status === 'error'`. */
  errorMessage?: string;
  /** Active search query (server-side substring). */
  search: string;
  /** Active filter chips. Default: all five kinds (no `kind=` query sent). */
  selectedKinds: ReadonlyArray<KbEntityKind>;
  /** Currently-selected entity in the tree (for the detail card tab). */
  selectedEntityId: string | undefined;
  /**
   * Entity ids that recently changed (within the last ~1 s) — drives the
   * pulse animation in `<KbEntityTree>`. Cleared per-id by `clearPulseFor`.
   */
  recentlyChangedEntityIds: ReadonlySet<string>;
}

/** Slice actions. */
export interface KbSliceActions {
  /** Updates `search`. Caller is responsible for debouncing the keystroke. */
  setSearch: (query: string) => void;
  /** Updates `selectedKinds` and triggers a refetch. */
  setSelectedKinds: (kinds: ReadonlyArray<KbEntityKind>) => void;
  /**
   * Refetches `entities` from the server using the current `search` +
   * `selectedKinds`. Aborts any in-flight refetch first.
   */
  refetchEntities: () => Promise<void>;
  /** Sets the selected entity id (drives the detail card). */
  selectEntity: (entityId: string | undefined) => void;
  /**
   * Applies a `kb:changesetApplied` payload. For each `applied` outcome the
   * matching entity id is added to `recentlyChangedEntityIds`; the SSE
   * handler caller schedules `setTimeout(() => clearPulseFor(id), 1000)`.
   * Failed outcomes are recorded in `errorMessage` (last-write-wins).
   *
   * Named `applyChangesetAppliedToKb` (not just `applyChangesetApplied`) so
   * the kb slice composes cleanly into the root store alongside the
   * changeset slice — both slices need to react to the same SSE event but
   * with different signatures (changeset slice takes `(changesetId,
   * outcomes)` and is the source of truth for entity-status flips).
   */
  applyChangesetAppliedToKb: (outcomes: ReadonlyArray<KbApplyOutcome>) => void;
  /** Removes an id from `recentlyChangedEntityIds` (called from the timer). */
  clearPulseFor: (entityId: string) => void;
  /**
   * Test/debug helper. Per-slice prefixed (MED-2) so the RootStore
   * composition no longer collides on a shared `_reset` name.
   */
  _resetKb: () => void;
}

export type KbSlice = KbSliceState & KbSliceActions;

export const KB_INITIAL_STATE: KbSliceState = {
  entities: [],
  status: 'idle',
  search: '',
  selectedKinds: DEFAULT_KINDS,
  selectedEntityId: undefined,
  recentlyChangedEntityIds: new Set(),
};

export interface KbSliceConfig {
  api?: KbApi;
  apiContext?: ApiContext;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/** Are all five kinds selected (i.e., the default — omit `kind=` entirely)? */
function allKindsSelected(kinds: ReadonlyArray<KbEntityKind>): boolean {
  if (kinds.length !== DEFAULT_KINDS.length) return false;
  for (const k of DEFAULT_KINDS) if (!kinds.includes(k)) return false;
  return true;
}

/**
 * Deduplicate entities by entityId — preserves first-seen order.
 */
function dedupeByEntityId(entities: KbEntity[]): KbEntity[] {
  const seen = new Set<string>();
  const out: KbEntity[] = [];
  for (const e of entities) {
    if (seen.has(e.entityId)) continue;
    seen.add(e.entityId);
    out.push(e);
  }
  return out;
}

export const createKbSlice =
  (config: KbSliceConfig = {}): StateCreator<KbSlice> =>
  (set, get) => {
    const api = config.api ?? defaultKbApi;
    const ctx = config.apiContext ?? defaultApiContext();
    let activeController: AbortController | null = null;

    return {
      ...KB_INITIAL_STATE,

      setSearch: (query) => {
        set({ search: query });
      },

      setSelectedKinds: (kinds) => {
        // Defensive copy — callers may pass a mutable array.
        set({ selectedKinds: [...kinds] });
      },

      refetchEntities: async () => {
        // Abort previous in-flight controller; install a fresh one.
        if (activeController !== null) activeController.abort();
        const controller = new AbortController();
        activeController = controller;

        const { selectedKinds, search } = get();
        set({ status: 'loading', errorMessage: undefined });

        const trimmedSearch = search.trim();
        const searchParam = trimmedSearch.length > 0 ? trimmedSearch : undefined;

        try {
          let merged: KbEntity[] = [];
          if (allKindsSelected(selectedKinds) || selectedKinds.length === 0) {
            const res = await api.listEntities(ctx, { search: searchParam });
            if (controller.signal.aborted) return;
            if (!res.ok) {
              set({ status: 'error', errorMessage: res.error.message });
              return;
            }
            merged = res.value.items;
          } else {
            // Fan out one request per kind; merge.
            const results = await Promise.all(
              selectedKinds.map((kind) => api.listEntities(ctx, { kind, search: searchParam })),
            );
            if (controller.signal.aborted) return;
            for (const res of results) {
              if (!res.ok) {
                set({ status: 'error', errorMessage: res.error.message });
                return;
              }
              merged = merged.concat(res.value.items);
            }
            merged = dedupeByEntityId(merged);
          }

          set({ entities: merged, status: 'ready', errorMessage: undefined });
        } finally {
          if (activeController === controller) activeController = null;
        }
      },

      selectEntity: (entityId) => {
        set({ selectedEntityId: entityId });
      },

      applyChangesetAppliedToKb: (outcomes) => {
        const prev = get().recentlyChangedEntityIds;
        const next = new Set(prev);
        let firstError: string | undefined;
        for (const outcome of outcomes) {
          if (outcome.status === 'applied') {
            next.add(outcome.entityId);
          } else if (outcome.status === 'failed' && firstError === undefined) {
            firstError = outcome.error?.message;
          }
        }
        set({
          recentlyChangedEntityIds: next,
          ...(firstError !== undefined ? { errorMessage: firstError } : {}),
        });
      },

      clearPulseFor: (entityId) => {
        const prev = get().recentlyChangedEntityIds;
        if (!prev.has(entityId)) return;
        const next = new Set(prev);
        next.delete(entityId);
        set({ recentlyChangedEntityIds: next });
      },

      _resetKb: () => {
        if (activeController !== null) {
          activeController.abort();
          activeController = null;
        }
        set({ ...KB_INITIAL_STATE });
      },
    };
  };

/** Vanilla-store factory for standalone use (tests, non-shell call-sites). */
export function createKbStore(config: KbSliceConfig = {}) {
  return createStore<KbSlice>()(createKbSlice(config));
}
